import { z } from 'zod';

export const IntegrationRequestSchema = z.object({
  platform: z.string(),
});

export const PostTileSettingsRequestSchema = z.object({
  tileId: z.string(),
  campaignCollectionId: z.string().optional(),
  channel: z.string().optional(),
  integrationId: z.string().optional(),
  adAccountId: z.string().optional(),
});

export const PostTileSettingsResponseSchema = z.object({
  data: z.object({
    message: z.nullable(z.string()).optional(),
    items: z.array(
      z.object({
        id: z.string(),
        tileId: z.string(),
        campaignCollectionId: z.string(),
        channel: z.string().nullable(),
        autoSyncEnabled: z.boolean(),
        integrationId: z.string().optional(),
        adAccountId: z.string().optional(),
      }),
    ),
    pageNumber: z.nullable(z.number()).optional(),
    itemsPerPage: z.nullable(z.number()).optional(),
    totalItems: z.nullable(z.number()).optional(),
    ordering: z.nullable(z.string()).optional(),
    additional: z.nullable(z.unknown()).optional(),
  }),
  error: z.nullable(z.string()).optional(),
});

export const DeleteTileSettingsResponseSchema = z.object({
  data: z.object({
    message: z.string(),
  }),
  error: z.nullable(z.string()).optional(),
});

export const CurrencyListTypeResponseSchema = z.object({
  data: z.object({
    items: z.array(
      z.object({
        code: z.string(),
        id: z.string(),
        type: z.enum(['secondary', 'main']),
      }),
    ),
  }),
  error: z.nullable(z.string()).optional(),
});

export const ExchangeRateRequestSchema = z.object({
  fromCurrency: z.string(),
  toCurrency: z.string(),
  dateFrom: z.string(),
  dateTo: z.string(),
});

export const ExchangeRateResponseSchema = z.object({
  rate: z.number(),
  type: z.string(),
});

export const SetCurrencyRequestSchema = z.object({
  integrationCurrency: z.string(),
});

export const ResponseString = z.string();

const BaseSettingsSchema = z.object({
  dailyBudget: z.number().or(z.string()).or(z.null()).nullable(),
  endDate: z.string().nullable(),
  executionStatus: z.string(),
  externalExecutionStatus: z.string().nullable(),
  externalExecutionStatusLabel: z.string().nullable(),
  externalId: z.string(),
  externalType: z.string().nullable(),
  externalTypeLabel: z.string().nullable(),
  id: z.string(),
  lastSyncAt: z.string().nullable(),
  lifetimeBudget: z.string().nullable(),
  locallyCreatedAt: z.string(),
  locallyUpdatedAt: z.string().nullable(),
  name: z.string(),
  platform: z.string(),
  startDate: z.string().nullable(),
  tileSettingsId: z.number().optional(),
  autoImport: z.boolean().optional(),
});

const BaseAgGridSettingsSchema = z.object({
  name: z.string(),
  externalId: z.string().optional(),
  externalType: z.string().nullable().optional(),
  externalExecutionStatus: z.string().nullable(),
  startDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
  dailyBudget: z.number().or(z.string()).or(z.null()).nullable().optional(),
  lifetimeBudget: z.string().nullable().optional(),
  autoImport: z.boolean().optional(),
  lastSyncAt: z.string().nullable(),
});

export const CampaignAgGridSettingsSchema = BaseAgGridSettingsSchema.extend({
  biddingType: z.string().nullable().optional(),
});

export const CampaignSettingsSchema = BaseSettingsSchema.extend({
  biddingType: z.string().nullable(),
});

export const AdGroupAgGridSettingsSchema = BaseAgGridSettingsSchema.extend({
  campaignId: z.string(),
  campaignName: z.string(),
});

export const AdAgGridSettingsSchema = BaseAgGridSettingsSchema.extend({
  adGroupId: z.string(),
  adGroupName: z.string(),
  campaignId: z.string(),
  campaignName: z.string(),
});

export const AdSettingsSchema = BaseSettingsSchema.extend({
  adGroupId: z.string(),
  adGroupName: z.string(),
  campaignId: z.string(),
  campaignName: z.string(),
});

export const CommonTotalsResponseSchema = z.object({
  data: z.object({
    message: z.string().nullable().optional(),
    items: z.array(
      z.object({
        dailyBudget: z.string(),
        lifetimeBudget: z.string(),
        startDate: z.string().nullable(),
        endDate: z.string().nullable(),
      }),
    ),
    pageNumber: z.number().nullable().optional(),
    itemsPerPage: z.number().nullable().optional(),
    totalItems: z.number().nullable().optional(),
    ordering: z.string().nullable().optional(),
    additional: z.unknown().nullable().optional(),
  }),
  error: z.string().nullable().optional(),
});

export const CampaignsDeleteRequestSchema = z.object({
  campaignIds: z.array(z.string()),
  platform: z.string(),
});

export const AdGroupDeleteRequestSchema = z.object({
  adGroupIds: z.array(z.string()),
  platform: z.string(),
});

export const AdDeleteRequestSchema = z.object({
  adIds: z.array(z.string()),
  platform: z.string(),
});

const AgGridColumnSchema = z.object({
  id: z.string(),
  label: z.string(),
  type: z.string(),
  isHidden: z.boolean(),
  isMandatory: z.boolean(),
  options: z
    .array(
      z.object({
        value: z.string(),
        label: z.string(),
      }),
    )
    .nullable(),
  disabledOptions: z
    .array(
      z.object({
        value: z.string(),
        label: z.string(),
      }),
    )
    .nullable(),
});

export const AgGridColumnsResponseSchema = z.object({
  data: z.object({
    items: z.array(AgGridColumnSchema),
  }),
  error: z.nullable(z.string()).optional(),
});

export const AgGridEntityRequestSchema = z.object({
  filters: z.looseObject({}),
  ordering: z.string().nullable(),
  startRow: z.number(),
  endRow: z.number(),
});

export const CampaignsResponseSchema = z.object({
  data: z.object({
    message: z.string().nullable().optional(),
    items: z.array(
      z.object({
        id: z.string(),
        attributes: CampaignAgGridSettingsSchema,
      }),
    ),
    itemsPerPage: z.number(),
    pageNumber: z.number(),
    totalItems: z.number(),
    ordering: z.string(),
    additional: z.unknown().nullable().optional(),
  }),
});

export const AdGroupsResponseSchema = z.object({
  data: z.object({
    message: z.string().nullable().optional(),
    items: z.array(
      z.object({
        id: z.string(),
        attributes: AdGroupAgGridSettingsSchema,
      }),
    ),
    itemsPerPage: z.number(),
    pageNumber: z.number(),
    totalItems: z.number(),
    ordering: z.string(),
    additional: z.unknown().nullable().optional(),
  }),
});

export const AdsResponseSchema = z.object({
  data: z.object({
    message: z.string().nullable().optional(),
    items: z.array(
      z.object({
        id: z.string(),
        attributes: AdAgGridSettingsSchema,
      }),
    ),
    itemsPerPage: z.number(),
    pageNumber: z.number(),
    totalItems: z.number(),
    ordering: z.string(),
    additional: z.unknown().nullable().optional(),
  }),
});

export const LaunchManualSyncRequestSchema = z.object({
  campaignIds: z.array(z.string()),
});

export const LaunchManualSyncResponseSchema = z.object({
  data: z.object({
    items: z.array(
      z.object({
        taskId: z.string(),
      }),
    ),
  }),
});

export const TextFilterCondition = z.enum([
  'contains',
  'not_contains',
  'equals',
  'not_equals',
  'starts_with',
  'ends_with',
]);

export const TextFilter = z.object({
  condition: TextFilterCondition,
  value: z.string().nullish(),
});

export const SetFilter = z.object({
  values: z.array(z.string()),
});

const ComparisonOperator = z.enum(['AND', 'OR']);

const MultiTextFilter = z.object({
  operator: ComparisonOperator,
  filters: z.array(TextFilter),
});

export const NameFilter = z.object({
  filters: z.array(SetFilter.or(MultiTextFilter).or(TextFilter)).optional(),
});

export const IdFilter = z.object({
  filters: z.array(SetFilter.or(MultiTextFilter).or(TextFilter)).optional(),
});

export const RequestFilterParams = z.looseObject({
  name: NameFilter.optional(),
  status: SetFilter.optional(),
  id: IdFilter.optional(),
});
